import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';
import { API_BASE_URL } from '../../config/api';
import { User } from '../../types';

type Timeframe = 'day' | 'week' | 'month';
type Direction = 'higher' | 'lower';
type MetricFormat = 'money' | 'number' | 'ratio' | 'percent';

interface KpiMetric {
  key: string;
  label: string;
  format: MetricFormat;
  direction: Direction;
  requires: string;
}

interface Kpi {
  _id: string;
  title: string;
  metric: string;
  metricLabel: string;
  target: number;
  timeframe: Timeframe;
  direction: Direction;
  note?: string;
  format: MetricFormat;
  value: number | null;
  previous: number | null;
  series: { date: string; value: number }[];
  configured: boolean;
  onTrack: boolean | null;
  progress: number | null;
}

const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  day: 'Today',
  week: 'This week',
  month: 'This month',
};

const fmt = (value: number | null | undefined, format: MetricFormat): string => {
  if (value == null || Number.isNaN(value)) return '—';
  switch (format) {
    case 'money':
      return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    case 'ratio':
      return `${value.toFixed(2)}x`;
    case 'percent':
      return `${value.toFixed(1)}%`;
    default:
      return value.toLocaleString();
  }
};

interface EditorState {
  id: string | null;
  title: string;
  metric: string;
  target: string;
  timeframe: Timeframe;
  direction: Direction;
  note: string;
}

const blankEditor = (metrics: KpiMetric[]): EditorState => {
  const first = metrics[0];
  return {
    id: null,
    title: '',
    metric: first?.key || 'revenue',
    target: '',
    timeframe: 'day',
    direction: first?.direction || 'higher',
    note: '',
  };
};

const KpiCard: React.FC<{ kpi: Kpi; isAdmin: boolean; onEdit: () => void; onDelete: () => void }> = ({
  kpi,
  isAdmin,
  onEdit,
  onDelete,
}) => {
  const delta = kpi.value != null && kpi.previous != null ? kpi.value - kpi.previous : null;
  const deltaPct =
    delta != null && kpi.previous ? (delta / Math.abs(kpi.previous)) * 100 : null;
  const good = delta == null ? null : kpi.direction === 'higher' ? delta >= 0 : delta <= 0;
  const progressClamped = kpi.progress != null ? Math.min(Math.max(kpi.progress, 0), 100) : 0;
  const barColor = kpi.onTrack ? '#059669' : '#f59e0b';

  return (
    <div className="card h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.04em' }}>
              {TIMEFRAME_LABELS[kpi.timeframe]} · {kpi.metricLabel}
            </div>
            <h3 className="mb-0" style={{ fontWeight: 700 }}>{kpi.title}</h3>
          </div>
          {isAdmin && (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit}>
                Edit
              </button>
              <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={onDelete}>
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="d-flex align-items-baseline gap-2 mt-2">
          <span style={{ fontSize: '2rem', fontWeight: 700 }}>{fmt(kpi.value, kpi.format)}</span>
          {delta != null && (
            <span className={good ? 'text-success' : 'text-danger'} style={{ fontWeight: 600 }}>
              <FontAwesomeIcon icon={delta >= 0 ? faArrowUp : faArrowDown} className="me-1" />
              {deltaPct != null ? `${Math.abs(deltaPct).toFixed(0)}%` : fmt(Math.abs(delta), kpi.format)}
            </span>
          )}
        </div>

        <div className="text-muted small">
          Target: <strong>{fmt(kpi.target, kpi.format)}</strong>
          {kpi.direction === 'lower' ? ' or lower' : ' or higher'}
        </div>

        <div className="progress mt-2" style={{ height: '8px' }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${progressClamped}%`, backgroundColor: barColor }}
            aria-valuenow={progressClamped}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="d-flex justify-content-between small mt-1">
          <span className={kpi.onTrack ? 'text-success' : 'text-warning'}>
            {kpi.onTrack == null ? 'No data' : kpi.onTrack ? 'On track' : 'Off target'}
          </span>
          <span className="text-muted">{kpi.progress != null ? `${kpi.progress.toFixed(0)}% of target` : ''}</span>
        </div>

        {kpi.series.length > 1 && (
          <div className="mt-3" style={{ height: '52px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={kpi.series}>
                <Tooltip
                  formatter={(v: number) => [fmt(v, kpi.format), kpi.metricLabel]}
                  labelFormatter={(l) => new Date(l).toLocaleDateString()}
                />
                <Line type="monotone" dataKey="value" stroke={barColor} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {!kpi.configured && (
          <div className="text-muted small mt-2">
            Source not connected yet — showing placeholder zeros.
          </div>
        )}
        {kpi.note && <div className="text-muted small mt-2 fst-italic">{kpi.note}</div>}
      </div>
    </div>
  );
};

const StoreDashboardKpiView: React.FC<{ user: User | null }> = ({ user }) => {
  const isAdmin = user?.role === 'admin';
  const [kpis, setKpis] = useState<Kpi[]>([]);
  const [metrics, setMetrics] = useState<KpiMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, metricRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/store-dashboard/kpis`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/store-dashboard/kpi-metrics`, { withCredentials: true }),
      ]);
      setKpis(kpiRes.data?.kpis || []);
      setMetrics(metricRes.data?.metrics || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => setEditor(blankEditor(metrics));
  const openEdit = (kpi: Kpi) =>
    setEditor({
      id: kpi._id,
      title: kpi.title,
      metric: kpi.metric,
      target: String(kpi.target),
      timeframe: kpi.timeframe,
      direction: kpi.direction,
      note: kpi.note || '',
    });

  const onMetricChange = (key: string) => {
    const def = metrics.find((m) => m.key === key);
    setEditor((cur) => (cur ? { ...cur, metric: key, direction: def?.direction || 'higher' } : cur));
  };

  const saveEditor = async () => {
    if (!editor) return;
    const body = {
      title: editor.title.trim(),
      metric: editor.metric,
      target: Number(editor.target),
      timeframe: editor.timeframe,
      direction: editor.direction,
      note: editor.note.trim(),
    };
    setSaving(true);
    try {
      if (editor.id) {
        await axios.put(`${API_BASE_URL}/store-dashboard/kpis/${editor.id}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API_BASE_URL}/store-dashboard/kpis`, body, { withCredentials: true });
      }
      setEditor(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save KPI');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this KPI?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/store-dashboard/kpis/${id}`, { withCredentials: true });
      setKpis((cur) => cur.filter((k) => k._id !== id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete KPI');
    }
  };

  const selectedMetric = editor ? metrics.find((m) => m.key === editor.metric) : undefined;

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">Fluora</div>
          <h1>KPIs</h1>
        </div>
      </div>

      <div className="alert alert-warning fw-semibold" role="alert">
        ⚠️ These controls are still in development — the data and visualizations may not be accurate.
      </div>

      <p className="store-dash-hint">
        Company-wide key performance indicators and their targets.
        {isAdmin ? ' As an admin you can add KPIs and set goals.' : ' Targets are managed by admins.'}
      </p>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {kpis.map((kpi) => (
            <div key={kpi._id} className="col-12 col-md-6 col-xl-4">
              <KpiCard kpi={kpi} isAdmin={!!isAdmin} onEdit={() => openEdit(kpi)} onDelete={() => remove(kpi._id)} />
            </div>
          ))}
          {kpis.length === 0 && (
            <div className="col-12">
              <div className="alert alert-info mb-0">No KPIs yet.</div>
            </div>
          )}
        </div>
      )}

      {isAdmin && editor && (
        <div className="card mt-4">
          <div className="card-body">
            <h5 className="card-title">{editor.id ? 'Edit KPI' : 'Add KPI'}</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Metric</label>
                <select className="form-select" value={editor.metric} onChange={(e) => onMetricChange(e.target.value)}>
                  {metrics.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Timeframe</label>
                <select
                  className="form-select"
                  value={editor.timeframe}
                  onChange={(e) => setEditor((cur) => (cur ? { ...cur, timeframe: e.target.value as Timeframe } : cur))}
                >
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Target{selectedMetric?.format === 'money' ? ' ($)' : selectedMetric?.format === 'ratio' ? ' (x)' : ''}
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={editor.target}
                  onChange={(e) => setEditor((cur) => (cur ? { ...cur, target: e.target.value } : cur))}
                  placeholder="e.g. 1000"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Goal direction</label>
                <select
                  className="form-select"
                  value={editor.direction}
                  onChange={(e) => setEditor((cur) => (cur ? { ...cur, direction: e.target.value as Direction } : cur))}
                >
                  <option value="higher">Higher is better</option>
                  <option value="lower">Lower is better</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={editor.title}
                  onChange={(e) => setEditor((cur) => (cur ? { ...cur, title: e.target.value } : cur))}
                  placeholder={selectedMetric?.label || 'KPI name'}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Note (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={editor.note}
                  onChange={(e) => setEditor((cur) => (cur ? { ...cur, note: e.target.value } : cur))}
                  placeholder="Context for this goal"
                />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditor(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveEditor} disabled={saving || !editor.target}>
                {saving ? 'Saving…' : editor.id ? 'Save changes' : 'Add KPI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdmin && !editor && (
        <div className="d-flex justify-content-center mt-4">
          <button type="button" className="btn btn-primary" onClick={openNew}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add KPI
          </button>
        </div>
      )}
    </div>
  );
};

export default StoreDashboardKpiView;
